import { useState, useEffect } from 'react';
import { LeadershipEntry, LeadershipCreatePayload, LeadershipUpdatePayload } from '../types/override.types';
import { companyOverrideService } from '../services/company-override.service';

export const useLeadership = (companyId: number) => {
  const [leaders, setLeaders] = useState<LeadershipEntry[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadLeaders = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const data = await companyOverrideService.listLeadership(companyId);
      setLeaders(data);
    } catch (err: any) {
      setError(err.message || 'Failed to load leadership data');
    } finally {
      setIsLoading(false);
    }
  };

  const createLeader = async (payload: LeadershipCreatePayload) => {
    setIsSubmitting(true);
    try {
      await companyOverrideService.createLeadership(companyId, payload);
      await loadLeaders();
    } finally {
      setIsSubmitting(false);
    }
  };

  const updateLeader = async (leaderId: string, payload: LeadershipUpdatePayload) => {
    setIsSubmitting(true);
    try {
      await companyOverrideService.updateLeadership(companyId, leaderId, payload);
      await loadLeaders();
    } finally {
      setIsSubmitting(false);
    }
  };

  const deleteLeader = async (leaderId: string) => {
    setIsSubmitting(true);
    try {
      await companyOverrideService.deleteLeadership(companyId, leaderId);
      await loadLeaders();
    } finally {
      setIsSubmitting(false);
    }
  };

  const uploadHeadshot = async (leaderId: string, file: File) => {
    setIsSubmitting(true);
    try {
      await companyOverrideService.uploadHeadshot(companyId, leaderId, file);
      await loadLeaders();
    } finally {
      setIsSubmitting(false);
    }
  };

  const deleteHeadshot = async (leaderId: string) => {
    setIsSubmitting(true);
    try {
      await companyOverrideService.deleteHeadshot(companyId, leaderId);
      await loadLeaders();
    } finally {
      setIsSubmitting(false);
    }
  };

  const importFromEnrichment = async () => {
    setIsSubmitting(true);
    try {
      const result = await companyOverrideService.importLeadershipFromEnrichment(companyId);
      await loadLeaders();
      return result;
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    if (companyId > 0) {
      loadLeaders();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  return {
    leaders,
    isLoading,
    isSubmitting,
    error,
    loadLeaders,
    createLeader,
    updateLeader,
    deleteLeader,
    uploadHeadshot,
    deleteHeadshot,
    importFromEnrichment,
  };
};
